import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from '@/hooks/use-toast';
import { ExportTemplate } from '@/lib/graphql/generated/graphql';
import { trackEvent } from '@/lib/mixpanel';
import { getDomainErrorMessage } from '@/lib/utils/domain-error-translator';

import { useDealSelectionExport } from './useDealSelectionExport';

export function useExportValidationErrors(
  dealId: string,
  dealName: string,
  anomalyTypesCount: number
) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { isExporting, downloadUrl, error, startExport } =
    useDealSelectionExport(dealId, ExportTemplate.ValidationErrors);
  const [isInteracting, setIsInteracting] = useState(false);
  const downloadedUrlRef = useRef<string | null>(null);

  const exportValidationErrors = useCallback(() => {
    trackEvent('VALIDATION_ERRORS_EXPORT_CLICKED', {
      dealId,
      anomalyTypesCount,
    });
    downloadedUrlRef.current = null;
    setIsInteracting(true);
    startExport();
  }, [dealId, anomalyTypesCount, startExport]);

  useEffect(() => {
    if (!isInteracting || !downloadUrl) return;
    if (downloadedUrlRef.current === downloadUrl) return;
    downloadedUrlRef.current = downloadUrl;

    fetch(downloadUrl)
      .then((res) => {
        if (!res.ok) throw new Error(t('general.error_download'));
        return res.blob();
      })
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const date = new Date().toISOString().slice(0, 10);
        a.download = `${dealName}-Erreurs-${date}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast({
          title: t('general.success_export'),
          description: t('general.success_export_csv_download'),
        });
      })
      .catch(() => {
        toast({
          title: t('general.error'),
          description: t('general.error_download'),
          variant: 'destructive',
        });
      })
      .finally(() => setIsInteracting(false));
  }, [downloadUrl, isInteracting, dealName, t, toast]);

  useEffect(() => {
    if (!error) return;
    toast({
      title: t('general.error'),
      description: getDomainErrorMessage(
        error.message,
        t,
        'components.alert_sidebar.export_errors_failed'
      ),
      variant: 'destructive',
    });
    setIsInteracting(false);
  }, [error, t, toast]);

  return { exportValidationErrors, isExporting: isExporting || isInteracting };
}
